import { memo, useContext, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setVehiclesStateData,
  updateVehicle,
  updateVehicleOnlineStatuses,
} from "Slices/vehiclesState";
import { useFMSWebsocket, OnlineStatusesData } from "Utils/webSocketUtils";
import { fetchGetVehicles } from "Slices/vehicles";
import { VEHICLE_STATE_WS, WS_SPINNER_LOGS } from "Utils/constants";
import { getCurrentSemanticMapId } from "Slices/appState";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:RealTimeMap`);

const UPDATING_FIELDS = [
  "t_long",
  "t_lat",
  "t_drive_mode",
  "t_web_status",
  "t_virtual_bumper_status",
  "t_vehicle_status",
  "last_contact",
];

export const RealTimeMapController = memo(() => {
  const semanticMapId = useSelector(getCurrentSemanticMapId);

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    []
  );

  useEffect(() => {
    if (semanticMapId) {
      wsSpinnerLogs.debug("new semantic map spinner on");
      setIsWSConnecting?.(true);
    } else {
      wsSpinnerLogs.debug("no semantic map spinner off");
      setIsWSConnecting?.(false);
    }
  }, [semanticMapId]);

  const dispatch = useDispatch();

  const handleVehicleStateCreateMessage = (data: any) => {
    wsSpinnerLogs.debug("create message spinner off");
    setIsWSConnecting?.(false);
    dispatch(setVehiclesStateData(data));
  };

  const handleVehicleStateUpdateMessage = (data: VehicleState) => {
    dispatch(updateVehicle(data));
  };

  const handleOnlineStatuses = (data: OnlineStatusesData) => {
    dispatch(updateVehicleOnlineStatuses(data));
  };

  useFMSWebsocket({
    url: "/vehicles/state/",
    subscriptionData: semanticMapId,
    loggerFlag: VEHICLE_STATE_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
      fields: UPDATING_FIELDS,
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
    }),
    handleInitialMessage: handleVehicleStateCreateMessage,
    handleMessage: handleVehicleStateUpdateMessage,
    handleOnlineStatuses,
    handleVehicleCreated: () => {
      // a vehicle was registered on the backend - refetch the canonical list
      // so the new vehicle renders without a page refresh
      dispatch(fetchGetVehicles());
    },
  });

  return null;
});
